import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Warning from "../../../../../Components/common/Warning/Warning";

import "./styles.scss";
import {
  publicKeyCheckRequest,
  publicKeyRegRequest,
  apiLimitterGetRequest,
  apiLimitterSetRequest,
} from "../../../../../store/actions/usersActions";

// Backend default for User.publicApiLimitter.
const DEFAULT_API_LIMITER = 100;

const PublicApi = () => {
  const userPublicKey = useSelector((state) => state?.users.publicKey);
  const apiLimiter = useSelector((state) => state?.users.apiLimiter);
  const user = useSelector((state) => state?.users.user);
  const [range, setRange] = useState(DEFAULT_API_LIMITER);
  const [tempRange, setTempRange] = useState(DEFAULT_API_LIMITER);
  const [isLoading, setIsLoading] = useState(true);
  const [showResetWarning, setShowResetWarning] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(publicKeyCheckRequest(user.username));
  }, [dispatch]);

  useEffect(() => {
    dispatch(apiLimitterGetRequest(user.username));
  }, [dispatch]);

  useEffect(() => {
    if (apiLimiter) {
      setRange(apiLimiter);
      setTempRange(apiLimiter);
      setIsLoading(false);
    }
  }, [apiLimiter]);

  const createPublicApiKey = () => {
    dispatch(publicKeyRegRequest(user.username));
  };

  const regeneratePublicApiKey = () => {
    if (
      window.confirm(
        "A new API key will be generated and the current key will stop working immediately. Any integration using the old key must be updated. Continue?",
      )
    ) {
      dispatch(publicKeyRegRequest(user.username));
    }
  };

  const saveRange = () => {
    if (tempRange !== apiLimiter) {
      const data = { tempRange: tempRange, user: user.username };
      dispatch(apiLimitterSetRequest(data));
      setRange(tempRange);
    }
  };

  const resetRange = () => {
    setShowResetWarning(false);
    setTempRange(DEFAULT_API_LIMITER);
    setRange(DEFAULT_API_LIMITER);
    dispatch(apiLimitterSetRequest({ tempRange: DEFAULT_API_LIMITER, user: user.username }));
  };

  return (
    <div className="publicapi">
      <h1 className="publicapi__header">Public API</h1>
      <div className="publicapi__body">
        <div className="publicapi__body__header">
          <h3>Public Api Key</h3>
          <p>
            On this page, you can view your public API key and adjust your public API usage per
            minute.
          </p>
        </div>
        <div className="publicapi__body__content">
          <div className="publicapi__body__content__row">
            <div className="publicapi__body__content__row__apikey">
              <p className="publicapi__body__content__row__apikey__header">Public API Key</p>
              <div className="publicapi__body__content__row__apikey__labels">
                {userPublicKey ? (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    <span style={{ wordBreak: "break-all" }}>{userPublicKey}</span>
                    <button className="primary-button" onClick={regeneratePublicApiKey}>
                      Regenerate
                    </button>
                  </div>
                ) : (
                  <button className="primary-button" onClick={createPublicApiKey}>
                    Create Key
                  </button>
                )}
              </div>
            </div>
          </div>
          <div>
            <p className="light publicapi__body__content__row__range-container-header">
              Public API Limiter ( Per minutes )
            </p>
            {isLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="publicapi__body__content__row__range-container">
                <div className="publicapi__body__content__row__range-container__data">
                  <div></div>
                  <input
                    type="number"
                    value={tempRange}
                    onChange={(e) => setTempRange(Number(e.target.value))}
                    min="1"
                    max="2000"
                    disabled={isLoading}
                  />
                  <div></div>
                </div>
                <input
                  type="range"
                  max="2000"
                  onChange={(e) => setTempRange(Number(e.target.value))}
                  value={tempRange}
                  disabled={isLoading}
                />
              </div>
            )}
            <div className="publicapi__body__content__actions">
              <button
                className="m-r-15 default-button"
                onClick={() => setShowResetWarning(true)}
                disabled={isLoading || Number(apiLimiter) === DEFAULT_API_LIMITER}>
                Reset
              </button>
              <button className="primary-button" onClick={saveRange} disabled={isLoading}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      <Warning
        onClickOk={resetRange}
        show={showResetWarning}
        setShow={setShowResetWarning}
        text={`The Public API limiter will be reset to ${DEFAULT_API_LIMITER} requests per minute. Do you want to continue?`}
        article="Reset"
      />
    </div>
  );
};

export default PublicApi;
